// client/src/pages/POA/AIAssistModal.tsx
// Lightweight chat modal for AI-assisted Form 2848 preparation.
// User describes what POA they need; the AI gathers data via tool calls
// and returns a complete form payload.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { X, Send, User, Loader } from 'lucide-react';
import { sendAiAssistMessage, type AiMessage, type AiResponse } from '../../services/poaAiAssist';

const KB_LOGO = 'https://storageacctbmssprod001.blob.core.windows.net/container-bmssprod001-public/images/KB-Favicon-New.png';

interface Props {
  onFormReady: (payload: Record<string, unknown>) => void;
  onClose:     () => void;
}

const INITIAL_HINT = `Describe the POA you need. For example:

> *I need a POA for client 00007-000 (both spouses) so I can discuss income on their 1040 for the next 5 years*

> *Create a POA for client 12345-000 — me and Bill Lorimer as reps to discuss income on their 1120-S*

The AI will look up the client, fetch their data, find your reps' credentials, and prepare the form.`;

// ── Scoped markdown styles for the chat ──────────────────────────────────────
const MD_STYLES = `
.poa-ai-md h1,.poa-ai-md h2,.poa-ai-md h3{font-weight:700;margin:12px 0 6px}
.poa-ai-md h2{font-size:1.05em;color:var(--accent);border-bottom:1px solid rgba(122,162,255,.15);padding-bottom:4px}
.poa-ai-md h3{font-size:.95em;color:#bb9af7}
.poa-ai-md p{margin:0 0 8px}
.poa-ai-md ul,.poa-ai-md ol{padding-left:1.4em;margin:0 0 8px}
.poa-ai-md li{margin:2px 0}
.poa-ai-md li::marker{color:var(--muted)}
.poa-ai-md strong{color:var(--text);font-weight:650}
.poa-ai-md em{color:#bb9af7;font-style:italic}
.poa-ai-md hr{border:none;border-top:1px solid rgba(122,162,255,.12);margin:12px 0}
.poa-ai-md blockquote{margin:8px 0;padding:6px 12px;border-left:3px solid rgba(122,162,255,.3);background:rgba(122,162,255,.04);border-radius:0 6px 6px 0;color:var(--muted);font-style:italic}
.poa-ai-md blockquote p{margin:0}
.poa-ai-md table{border-collapse:collapse;width:100%;font-size:12px;margin:10px 0}
.poa-ai-md th{background:rgba(0,0,0,.2);font-weight:650;color:var(--muted);text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.05em}
.poa-ai-md th,.poa-ai-md td{border:1px solid var(--border);padding:6px 10px}
.poa-ai-md tr:nth-child(even) td{background:rgba(255,255,255,.02)}
.poa-ai-md code{background:rgba(0,0,0,.25);padding:.1em .3em;border-radius:4px;font-size:85%;color:#bb9af7}
.poa-ai-md pre{background:rgba(0,0,0,.3);border:1px solid var(--border);border-radius:8px;padding:10px 14px;overflow-x:auto;margin:8px 0}
.poa-ai-md pre code{background:none;padding:0;color:var(--text)}
.poa-ai-md a{color:var(--accent)}

@keyframes poa-ai-gradient {
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
@keyframes poa-ai-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
`;

export default function AIAssistModal({ onFormReady, onClose }: Props) {
  const [messages,  setMessages]  = useState<AiMessage[]>([]);
  const [input,     setInput]     = useState('');
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [status,    setStatus]    = useState('');
  const chatEndRef  = useRef<HTMLDivElement>(null);
  const inputRef    = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg: AiMessage = { role: 'user', content: text };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setError(null);
    setLoading(true);
    setStatus('Thinking...');

    const hintTimer  = setTimeout(() => setStatus('Searching and fetching data from CCH...'), 4000);
    const hintTimer2 = setTimeout(() => setStatus('Still working — CCH exports can take 30+ seconds...'), 20000);

    try {
      const response: AiResponse = await sendAiAssistMessage(updatedMessages);
      clearTimeout(hintTimer);
      clearTimeout(hintTimer2);

      if (response.type === 'form') {
        const assistantMsg: AiMessage = { role: 'assistant', content: response.message || 'Form prepared successfully!' };
        setMessages([...updatedMessages, assistantMsg]);
        setTimeout(() => onFormReady(response.payload), 800);
      } else {
        const assistantMsg: AiMessage = { role: 'assistant', content: response.text };
        setMessages([...updatedMessages, assistantMsg]);
      }
    } catch (err: any) {
      clearTimeout(hintTimer);
      clearTimeout(hintTimer2);
      setError(err.message);
    } finally {
      setLoading(false);
      setStatus('');
    }
  }, [input, messages, loading, onFormReady]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }

  const gradientBar: React.CSSProperties = {
    height: 2,
    background: 'linear-gradient(90deg, #7aa2f7, #bb9af7, #9ece6a, #ff9e64, #7aa2f7)',
    backgroundSize: '200% 100%',
    animation: 'poa-ai-gradient 4s ease infinite',
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(0,0,0,.75)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <style>{MD_STYLES}</style>

      <div style={{
        background: '#1a1b2e',
        border: '1px solid rgba(122,162,255,.15)',
        borderRadius: 16, width: 640, maxWidth: '96vw', height: '72vh', maxHeight: 720,
        boxShadow: '0 24px 80px rgba(0,0,0,.6), 0 0 0 1px rgba(122,162,255,.06)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '14px 20px',
          background: '#252740',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 12,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(122,162,255,.08)', border: '1px solid rgba(122,162,255,.15)',
              boxShadow: '0 2px 8px rgba(0,0,0,.2)',
            }}>
              <img src={KB_LOGO} alt="Botfield" width={28} height={28} style={{ borderRadius: 6 }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, letterSpacing: '.2px' }}>AI POA Assistant</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>Powered by Admin HQ</div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.08)',
              borderRadius: 8, cursor: 'pointer', color: 'var(--muted)', padding: '6px 7px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background .15s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,.1)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,.05)')}
          >
            <X size={16} />
          </button>
        </div>

        <div style={{ height: 1, background: 'rgba(122,162,255,.08)' }} />

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {/* Initial hint */}
          {messages.length === 0 && (
            <div className="poa-ai-md" style={{
              padding: '18px 18px', borderRadius: 12,
              background: 'rgba(122,162,255,.04)', border: '1px solid rgba(122,162,255,.1)',
              fontSize: 13, color: 'var(--muted)', lineHeight: 1.7,
            }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{INITIAL_HINT}</ReactMarkdown>
            </div>
          )}

          {messages.map((msg, i) => (
            <div
              key={i}
              style={{
                display: 'flex', gap: 10, marginBottom: 14,
                flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
              }}
            >
              {msg.role === 'user' ? (
                <div style={{
                  width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'rgba(158,206,106,.12)', border: '1px solid rgba(158,206,106,.2)',
                }}>
                  <User size={14} color="#9ece6a" />
                </div>
              ) : (
                <div style={{
                  width: 28, height: 28, borderRadius: 8, flexShrink: 0, overflow: 'hidden',
                  border: '1px solid rgba(122,162,255,.15)',
                }}>
                  <img src={KB_LOGO} alt="" width={28} height={28} />
                </div>
              )}
              {msg.role === 'user' ? (
                <div style={{
                  maxWidth: '80%', padding: '10px 14px', borderRadius: 12,
                  background: 'rgba(158,206,106,.06)', border: '1px solid rgba(158,206,106,.15)',
                  fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap',
                }}>
                  {msg.content}
                </div>
              ) : (
                <div className="poa-ai-md" style={{
                  maxWidth: '85%', padding: '10px 14px', borderRadius: 12,
                  background: 'rgba(122,162,255,.05)', border: '1px solid rgba(122,162,255,.1)',
                  fontSize: 13, lineHeight: 1.6,
                }}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                </div>
              )}
            </div>
          ))}

          {/* Loading indicator */}
          {loading && (
            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8, flexShrink: 0, overflow: 'hidden',
                border: '1px solid rgba(122,162,255,.15)',
              }}>
                <img src={KB_LOGO} alt="" width={28} height={28} />
              </div>
              <div style={{
                padding: '10px 14px', borderRadius: 12,
                background: 'rgba(122,162,255,.05)', border: '1px solid rgba(122,162,255,.1)',
                fontSize: 13, color: 'var(--muted)',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <Loader size={14} style={{ animation: 'poa-ai-spin 1s linear infinite' }} />
                {status}
              </div>
            </div>
          )}

          {error && (
            <div style={{
              padding: '10px 14px', borderRadius: 10, fontSize: 13,
              background: 'rgba(255,107,107,.06)', border: '1px solid rgba(255,107,107,.2)',
              color: 'var(--bad)', marginBottom: 14,
            }}>
              {error}
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        {/* Bottom gradient bar */}
        <div style={gradientBar} />

        {/* Input area */}
        <div style={{
          padding: '14px 20px 18px',
          background: 'linear-gradient(0deg, rgba(122,162,255,.04) 0%, transparent 100%)',
          display: 'flex', gap: 10, alignItems: 'flex-end',
        }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe the POA you need..."
            disabled={loading}
            rows={2}
            style={{
              flex: 1, padding: '10px 14px', borderRadius: 12,
              border: '1px solid rgba(122,162,255,.12)',
              background: 'rgba(255,255,255,.03)',
              color: 'var(--text)', fontSize: 14, outline: 'none',
              fontFamily: 'inherit', resize: 'none', lineHeight: 1.5,
              boxSizing: 'border-box',
              opacity: loading ? 0.5 : 1,
              transition: 'border-color .2s',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(122,162,255,.3)')}
            onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(122,162,255,.12)')}
          />
          <button
            onClick={handleSend}
            disabled={loading || !input.trim()}
            style={{
              padding: '10px 14px', borderRadius: 12,
              border: '1px solid rgba(122,162,255,.2)',
              cursor: input.trim() && !loading ? 'pointer' : 'default',
              background: input.trim() && !loading
                ? 'linear-gradient(135deg, rgba(122,162,255,.15), rgba(187,154,247,.15))'
                : 'rgba(255,255,255,.03)',
              color: input.trim() && !loading ? '#7aa2f7' : 'var(--muted)',
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600,
              fontFamily: 'inherit',
              transition: 'background .2s, border-color .2s',
              opacity: loading ? 0.4 : 1,
            }}
          >
            <Send size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}
